import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'redux-first-router-link'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import ProgressDots from '@groovehq/internal-design-system/lib/components/ProgressDots/ProgressDots'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import Textarea from '@groovehq/internal-design-system/lib/components/Textarea/Textarea'
import Switch from '@groovehq/internal-design-system/lib/components/Switch/Switch'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { doCreateBulkInvitations } from 'ducks/agents/operations'
import { pluralize } from 'util/strings'
import { useRhfDirtyHold } from 'util/dirtyHolds'
import { convertEmailsToAgents } from 'ducks/agents/utils'
import {
  useRedirectToChannel,
  useRebuildLeftNavMenu,
} from 'ducks/channels/hooks'
import { INBOX } from 'ducks/billing/productTypes'
import { FEATURE_INBOX_MAX_USERS } from 'ducks/billing/featureTypes'
import { useProductFeature } from 'ducks/billing/hooks'
import { selectIsInInbox } from 'selectors/location'
import { SETTINGS_BILLING_PAGE } from 'subapps/settings/types'
import { styles } from './styles'
import { useAdminAccess } from '../../NoAccess/hooks'
import { CHANNEL_EMAIL_TYPES } from '../Channels.data'

const TO_SETTINGS_BILLING_PAGE = {
  type: SETTINGS_BILLING_PAGE,
  payload: {
    tab: 'plans',
  },
}

const BULK_FORM_SCHEMA = yup.object().shape({
  bulkEmails: yup
    .string()
    .trim()
    .lowercase()
    .required(`${app.t('Agents')} emails are required`),
  bulkRoleIsAdmin: yup.boolean().required('Role is required'),
})

const AddMembers = ({
  drawerId,
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  onExit,
}) => {
  const dispatch = useDispatch()

  const {
    handleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
    register,
    setError,
    control,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(BULK_FORM_SCHEMA),
    defaultValues: {
      bulkRoleIsAdmin: false,
      bulkEmails: '',
    },
  })

  const { releaseHold } = useRhfDirtyHold(drawerId, control)
  const isInInbox = useSelector(selectIsInInbox)
  useAdminAccess(onExit)

  const { rebuildExitAndRedirect } = useRedirectToChannel({
    channelId,
    onExit,
  })

  const { rebuildMenuAndExit } = useRebuildLeftNavMenu({ channelId, onExit })
  const [exceededLimit, setExceededLimit] = useState(false)
  const { canUseFeature, featureLimit, currentUsage } = useProductFeature(
    INBOX,
    FEATURE_INBOX_MAX_USERS
  )

  const handleOnExit = useCallback(
    () => {
      // we do not rebuild left nav as soon as channel is created anymore
      // if the user clicks the Exit button on top right, rebuild nav
      if (isInInbox) {
        rebuildExitAndRedirect()
        return
      }
      rebuildMenuAndExit()
    },
    [isInInbox, rebuildMenuAndExit, rebuildExitAndRedirect]
  )

  const onSubmit = useCallback(
    async data => {
      const { bulkEmails, bulkRoleIsAdmin } = data
      const { users, splitBulkEmails } = convertEmailsToAgents(bulkEmails, {
        bulkRoleIsAdmin,
        agentIdPrefix: channelId,
      })
      if (users.length > 0 && users.length === splitBulkEmails.length) {
        const invitedPlusCurrentUsers = users.length + currentUsage

        if (
          featureLimit !== 'unlimited' &&
          featureLimit < invitedPlusCurrentUsers
        ) {
          setError('bulkEmails', {
            type: 'manual',
          })

          setExceededLimit(true)

          return
        }

        releaseHold()
        await dispatch(
          doCreateBulkInvitations(users, [channelId], {
            shouldFetchAllAgents: true,
          })
        )
        handleOnExit()
      } else {
        setError('bulkEmails', {
          type: 'manual',
          message: `Invalid ${app.t('agent')} email ${pluralize(
            splitBulkEmails.length,
            'address',
            'es'
          )} detected`,
        })
      }
    },
    [
      dispatch,
      channelId,
      setError,
      releaseHold,
      handleOnExit,
      currentUsage,
      featureLimit,
    ]
  )

  const upgradeNotice = (
    <div
      css={[text.styles.textXs, text.styles.fontMedium, text.styles.textWhite]}
    >
      Your plan only allows {featureLimit}{' '}
      {app.t(pluralize(featureLimit, 'agent'))} on your account
      <br />
      Upgrade to add more
      <Button
        as={Link}
        to={TO_SETTINGS_BILLING_PAGE}
        type="info"
        size="small"
        className="grui px-12 py-3 mt-6"
      >
        See Plans
      </Button>
    </div>
  )

  return (
    <Modal
      onClose={handleOnExit}
      portal="#drawer-root"
      open
      dataTestId="channels-members-add"
      backdropTransparency="light"
    >
      <ProgressDots
        count={CHANNEL_EMAIL_TYPES[channelType].steps}
        now={CHANNEL_EMAIL_TYPES[channelType].steps}
        className="grui mt-4 mb-13"
      />
      <Modal.Title>Invite a {app.t('agent')}</Modal.Title>
      <Modal.Description className="grui px-4">
        Enter each {app.t('agent_possessive')} email address separated by{` `}
        commas, tabs or on new lines.
      </Modal.Description>
      <form
        css={styles.content}
        className="grui pt-10 pb-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <Textarea
            {...register('bulkEmails')}
            className="grui my-7 mw-100"
            css={styles.textArea}
            rows="10"
            placeholder={`name@email.com\nname@email.com\nname@email.com`}
            validateStatus={errors?.bulkEmails ? 'error' : undefined}
            help={errors?.bulkEmails?.message}
          />
        </div>
        <div className="grui mt-12">
          <span
            css={[fieldStyles.labelBox, text.styles.textSm]}
            className="grui mb-0"
          >
            Set as admin
          </span>
          <Switch
            id="roleSelection"
            name="roleSelection"
            css={text.styles.textXs}
            {...register('bulkRoleIsAdmin')}
          >
            Admins can add new {app.t('agents')} and edit channel & company{` `}
            settings
          </Switch>
        </div>
        <Tooltip
          title={upgradeNotice}
          interactiveTooltip
          strategy="fixed"
          className="grui pt-6 pb-11 px-11"
          disabled={!exceededLimit}
        >
          <div>
            <Modal.Button
              type="info"
              disabled={
                !isValid ||
                !isDirty ||
                isSubmitting ||
                !canUseFeature ||
                exceededLimit
              }
              className="grui mt-14"
              htmlType="submit"
            >
              <>
                {isSubmitting ? (
                  <span>
                    Saving<AnimatedEllipsis />
                  </span>
                ) : (
                  'Send invites & close'
                )}
              </>
            </Modal.Button>
          </div>
        </Tooltip>
        <Modal.Button
          type="tertiary"
          onClick={handleOnExit}
          className="grui mt-5"
        >
          Cancel & close
        </Modal.Button>
      </form>
    </Modal>
  )
}

export default AddMembers
